import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { isHiddenByStatus } from '../../utils/ticketFilters';
import useAutoCache from '../../hooks/useAutoCache';
import useTicketSync from './useTicketSync';

// #529 (Scheibe 4 aus #313): Die beiden Ticketlisten im Dashboard ("Meine"
// und Pool) -- abgeleitet aus originalTickets, mit Blacklist, optimistischer
// Unterdrueckung, Auto-Cache, den Ereignissen aus dem Heute-Kalender und den
// geplanten Tickets ohne Termin fuer die Terminliste. Code unveraendert aus
// Dashboard.js uebernommen, Muster aus #312/#471.
//
// Die Kette ueber die Funktionsidentitaet bleibt erhalten: Aendern sich
// currentUser oder ticketFilterSettings, entstehen neue Filter, ein neuer
// handleTicketUpdate und damit (im echten useAutoCache) ein neues
// manualRefreshTickets; Poller, Sync-Intervall und Listener starten neu.
// Dashboard.scheiben.test.js haelt das fest.
//
// myTickets und tickets werden an mehreren Stellen paarweise gepatcht. Das
// ist bewusst nicht bereinigt (dann waere es kein Umzug mehr).
export default function useDashboardTickets({ currentUser, getCurrentUserEmail, ticketFilterSettings }) {
  const [tickets, setTickets] = useState([]);
  const [ticketSearchFilter, setTicketSearchFilter] = useState('');
  // NEW: Separate state for "Meine Tickets" and its search filter
  const [myTickets, setMyTickets] = useState([]);
  const [myTicketSearchFilter, setMyTicketSearchFilter] = useState('');
  // Optimistische Unterdrückung (verhindert kurzfristiges Wiederauftauchen durch Serververzögerungen)
  const optimisticHiddenIdsRef = useRef(new Map()); // id(string) -> expiresTs

  // Store original unfiltered tickets for re-filtering
  const [originalTickets, setOriginalTickets] = useState([]);

  // Helper function: common blacklist keyword filtering used by both lists
  const applyKeywordBlacklist = useCallback((ticket) => {
    if (!ticketFilterSettings?.keywords || !ticketFilterSettings.keywords.trim()) return true;
    const keywords = ticketFilterSettings.keywords
      .split('\n')
      .map(k => k.trim().toLowerCase())
      .filter(k => k.length > 0);
    if (keywords.length === 0) return true;

    const title = (ticket.title || '').toLowerCase();
    const description = (ticket.description || '').toLowerCase();
    const category = (ticket.category || '').toLowerCase();

    const hasKeyword = keywords.some(keyword =>
      title.includes(keyword) || description.includes(keyword) || category.includes(keyword)
    );
    // Blacklist: exclude tickets containing keywords
    return !hasKeyword;
  }, [ticketFilterSettings]);

  // NEW: Filtering for "Meine Tickets" (always only assigned to current user)
  const filterTicketsAssignedToMe = useCallback((ticketsToFilter) => {
    const currentUserEmail = getCurrentUserEmail();
    return ticketsToFilter
      .filter(ticket => {
        // Optimistische Unterdrückung berücksichtigen
        try {
          const idStr = String(ticket.id);
          const exp = optimisticHiddenIdsRef.current.get(idStr);
          if (exp && exp > Date.now()) return false;
        } catch (_) {}
        const isAssignedToCurrentUser = currentUserEmail && ticket.assignedTo &&
          ticket.assignedTo.localeCompare(currentUserEmail, undefined, { sensitivity: 'base' }) === 0;
        // For assignee perspective: consider any red-corner signal (emails, comments, attachments, new ticket)
        const hasRedCornerForAssignee = isAssignedToCurrentUser && (
          ticket.isNewTicketForAssignee ||
          ticket.hasNewActivityForAssignee ||
          ticket.hasNewEmailsForAssignee ||
          ticket.hasNewCommentsForAssignee ||
          ticket.hasNewAttachmentsForAssignee
        );
        if (isHiddenByStatus(ticket)) {
          return false;
        }
        // Exclude tickets that are planned today or in the future (calendar scheduled but not started yet)
        // BUT keep them if they have new activity (red corner) for the assignee
        if (ticket.isPlanned && ticket.plannedStartDateTime) {
          const plannedStart = new Date(ticket.plannedStartDateTime);
          const plannedStartTs = plannedStart.getTime();
          const todayStart = new Date();
          todayStart.setHours(0, 0, 0, 0);
          if (!Number.isNaN(plannedStartTs) && plannedStartTs >= todayStart.getTime() && !hasRedCornerForAssignee) {
            return false;
          }
        }
        // Only tickets assigned to current user
        if (!isAssignedToCurrentUser) return false;
        // Project/Subproject: suppress unless exception applies
        if (ticket.projectId || ticket.subprojectId) {
          const nowTs = Date.now();
          const startTs = ticket.plannedStartDateTime ? new Date(ticket.plannedStartDateTime).getTime() : NaN;
          const endTs = ticket.plannedEndDateTime ? new Date(ticket.plannedEndDateTime).getTime() : NaN;
          const isPast = (!Number.isNaN(endTs) ? endTs < nowTs : (!Number.isNaN(startTs) ? startTs < nowTs : false));
          const showByPast = ticket.status !== 'abgeschlossen' && isPast;
          const showByRedCorner = !!hasRedCornerForAssignee;
          const showByNotPlanned = !ticket.isPlanned;
          if (!(showByPast || showByNotPlanned || showByRedCorner)) {
            return false;
          }
        }
        // Apply keyword blacklist
        return applyKeywordBlacklist(ticket);
      })
      .sort((a, b) => {
        // 1. Tickets mit roter Ecke (neue Aktivität) haben Vorrang
        const aHasNewActivity = (a.hasNewActivityForAssignee || a.hasNewEmailsForAssignee) || (a.hasNewActivity || a.hasNewEmails);
        const bHasNewActivity = (b.hasNewActivityForAssignee || b.hasNewEmailsForAssignee) || (b.hasNewActivity || b.hasNewEmails);
        if (aHasNewActivity && !bHasNewActivity) return -1;
        if (!aHasNewActivity && bHasNewActivity) return 1;
        
        // 2. Wartend-Status nach unten (aber nur außerhalb der roten Ecke)
        if (!aHasNewActivity && !bHasNewActivity) {
          const aIsWaiting = a.status === 'wartend';
          const bIsWaiting = b.status === 'wartend';
          if (!aIsWaiting && bIsWaiting) return -1;
          if (aIsWaiting && !bIsWaiting) return 1;
        }
        
        // 3. Nach Priorität (kritisch > hoch > normal > niedrig)
        const priorityOrder = { 'kritisch': 1, 'hoch': 2, 'normal': 3, 'niedrig': 4 };
        const priorityA = priorityOrder[a.priority] || 5;
        const priorityB = priorityOrder[b.priority] || 5;
        if (priorityA !== priorityB) return priorityA - priorityB;
        
        // 4. Innerhalb gleicher Priorität: Älteste Aktivität zuerst
        const aActivityTime = a.lastActivityAt ? new Date(a.lastActivityAt).getTime() : new Date(a.createdAt || 0).getTime();
        const bActivityTime = b.lastActivityAt ? new Date(b.lastActivityAt).getTime() : new Date(b.createdAt || 0).getTime();
        return aActivityTime - bActivityTime;
      });
  }, [applyKeywordBlacklist, getCurrentUserEmail]);


  // NEW: Filtering for "Tickets" that excludes current user's tickets
  const filterTicketsNotAssignedToMe = useCallback((ticketsToFilter) => {
    const currentUserEmail = getCurrentUserEmail();
    return ticketsToFilter
      .filter(ticket => {
        // Optimistische Unterdrückung berücksichtigen
        try {
          const idStr = String(ticket.id);
          const exp = optimisticHiddenIdsRef.current.get(idStr);
          if (exp && exp > Date.now()) return false;
        } catch (_) {}
        // Statusfilter zentral (W0-6): abgeschlossen = raus, ohne Aktivitäts-Ausnahme
        if (isHiddenByStatus(ticket)) {
          return false;
        }
        const isUnassigned = !ticket.assignedTo || ticket.assignedTo.trim() === '';
        const isAssignedToCurrentUser = currentUserEmail && ticket.assignedTo &&
          ticket.assignedTo.localeCompare(currentUserEmail, undefined, { sensitivity: 'base' }) === 0;
        // Exclude tickets assigned to current user
        if (isAssignedToCurrentUser) return false;
        // Show only unassigned tickets (exclude all assigned tickets)
        let willShow = isUnassigned;

        // Project/Subproject: Unassigned tickets immer anzeigen (keine zusätzliche Unterdrückung)
        // Früher wurden Projekt/Unterprojekt-Tickets auch bei unassigned teilweise ausgeblendet
        // Jetzt: Unassigned Tickets werden immer angezeigt, unabhängig von Projekt-Verknüpfung

        // Respect keyword blacklist
        if (willShow) {
          willShow = applyKeywordBlacklist(ticket);
        }
        return willShow;
      })
      .sort((a, b) => {
        // 1. Tickets mit roter Ecke (neue Aktivität) haben Vorrang
        const aHasNewActivity = a.hasNewActivity || a.hasNewEmails;
        const bHasNewActivity = b.hasNewActivity || b.hasNewEmails;
        if (aHasNewActivity && !bHasNewActivity) return -1;
        if (!aHasNewActivity && bHasNewActivity) return 1;
        
        // 2. Wartend-Status nach unten (aber nur außerhalb der roten Ecke)
        if (!aHasNewActivity && !bHasNewActivity) {
          const aIsWaiting = a.status === 'wartend';
          const bIsWaiting = b.status === 'wartend';
          if (!aIsWaiting && bIsWaiting) return -1;
          if (aIsWaiting && !bIsWaiting) return 1;
        }
        
        // 3. Nach Priorität (kritisch > hoch > normal > niedrig)
        const priorityOrder = { 'kritisch': 1, 'hoch': 2, 'normal': 3, 'niedrig': 4 };
        const priorityA = priorityOrder[a.priority] || 5;
        const priorityB = priorityOrder[b.priority] || 5;
        if (priorityA !== priorityB) return priorityA - priorityB;
        
        // 4. Innerhalb gleicher Priorität: Älteste Aktivität zuerst
        const aActivityTime = a.lastActivityAt ? new Date(a.lastActivityAt).getTime() : new Date(a.createdAt || 0).getTime();
        const bActivityTime = b.lastActivityAt ? new Date(b.lastActivityAt).getTime() : new Date(b.createdAt || 0).getTime();
        return aActivityTime - bActivityTime;
      });
  }, [applyKeywordBlacklist, getCurrentUserEmail]);

  // Re-filter tickets when currentUser is loaded or filter settings change
  useEffect(() => {
    if (currentUser && originalTickets.length > 0) {
      const filteredMine = filterTicketsAssignedToMe(originalTickets);
      setMyTickets(filteredMine);
      const filteredOthers = filterTicketsNotAssignedToMe(originalTickets);
      setTickets(filteredOthers);
    }
  }, [currentUser, originalTickets, filterTicketsAssignedToMe, filterTicketsNotAssignedToMe]);

  // Handle ticket data updates
  const handleTicketUpdate = useCallback((newTickets) => {
    // Store original tickets for re-filtering when user is loaded
    setOriginalTickets(newTickets);
    // Split into "Meine Tickets" and the rest
    const mine = filterTicketsAssignedToMe(newTickets);
    setMyTickets(mine);
    const others = filterTicketsNotAssignedToMe(newTickets);
    setTickets(others);
  }, [filterTicketsAssignedToMe, filterTicketsNotAssignedToMe]);

  const { manualRefresh: manualRefreshTickets, errorCount: ticketErrorCount, isSuspended: ticketsSuspended, status: ticketsStatus } = useAutoCache({
    type: 'tickets',
    enabled: true,
    onDataUpdate: handleTicketUpdate,
    checkInterval: 60000 // Check every 60 seconds (reduced from 15s to prevent pool exhaustion)
  });

  // #529: Kalender-Abgleich alle 60 s und Reparatur verwaister Planungen
  // (useTicketSync.js). Hier aufgerufen, damit sie direkt am manualRefresh
  // der Tickets haengen wie vorher.
  useTicketSync({ currentUser, manualRefreshTickets });

  // Sofortige, optimistische Aktualisierung der Ticketlisten bei Plan/Unplan aus DayCalendarPanel
  useEffect(() => {
    const onTicketsUpdated = (e) => {
      try {
        const detail = e?.detail || {};
        const action = detail.action || null;
        const updates = Array.isArray(detail.tickets) ? detail.tickets : [];
        if (!updates || updates.length === 0) return;

        // IDs robust normalisieren (String-Vergleich vermeidet Typ-Mismatches)
        const idSet = new Set(updates.map(t => String(t.id)));

        if (action === 'planned') {
          // IDs für kurze Zeit unterdrücken (z. B. 30s), bis Serverdaten sicher nachgezogen sind
          const until = Date.now() + 30000;
          updates.forEach(u => { try { optimisticHiddenIdsRef.current.set(String(u.id), until); } catch (_) {} });
          // Geplante Tickets sofort aus Pool/Meine entfernen
          setMyTickets(prev => prev.filter(t => !idSet.has(String(t.id))));
          setTickets(prev => prev.filter(t => !idSet.has(String(t.id))));
          return;
        }

        if (action === 'unplanned') {
          // Entplante Tickets sofort passend hinzufügen
          updates.forEach((u) => {
            const ticket = { ...u, isPlanned: false };
            // Unterdrückung für diese ID sofort aufheben
            try { optimisticHiddenIdsRef.current.delete(String(ticket.id)); } catch (_) {}
            const isUnassigned = !ticket.assignedTo || ticket.assignedTo.trim() === '';
            const currentUserEmail = getCurrentUserEmail();
            const isAssignedToCurrentUser = currentUserEmail && ticket.assignedTo &&
              ticket.assignedTo.localeCompare(currentUserEmail, undefined, { sensitivity: 'base' }) === 0;

            // Blacklist der Keywords respektieren
            const passesBlacklist = applyKeywordBlacklist(ticket);
            if (!passesBlacklist) {
              // In beiden Listen entfernen, falls vorhanden
              setMyTickets(prev => prev.filter(t => String(t.id) !== String(ticket.id)));
              setTickets(prev => prev.filter(t => String(t.id) !== String(ticket.id)));
              return;
            }

            if (isAssignedToCurrentUser) {
              setMyTickets(prev => (prev.some(t => String(t.id) === String(ticket.id)) ? prev : [...prev, ticket]));
              setTickets(prev => prev.filter(t => String(t.id) !== String(ticket.id)));
            } else if (isUnassigned) {
              setTickets(prev => (prev.some(t => String(t.id) === String(ticket.id)) ? prev : [...prev, ticket]));
              setMyTickets(prev => prev.filter(t => String(t.id) !== String(ticket.id)));
            } else {
              // Anderen zugewiesen → überall entfernen
              setMyTickets(prev => prev.filter(t => String(t.id) !== String(ticket.id)));
              setTickets(prev => prev.filter(t => String(t.id) !== String(ticket.id)));
            }
          });
          return;
        }
      } catch (_) {}
    };

    try { window.addEventListener('ticketsUpdated', onTicketsUpdated); } catch (_) {}
    return () => { try { window.removeEventListener('ticketsUpdated', onTicketsUpdated); } catch (_) {} };
  }, [applyKeywordBlacklist, getCurrentUserEmail]);

  // Cleanup-Intervall für abgelaufene Unterdrückungen
  useEffect(() => {
    const iv = setInterval(() => {
      try {
        const now = Date.now();
        const map = optimisticHiddenIdsRef.current;
        let changed = false;
        for (const [id, exp] of map.entries()) {
          if (!exp || exp <= now) { map.delete(id); changed = true; }
        }
        if (changed) {
          // Refilter gegen aktuelle Originaldaten, falls vorhanden
          if (originalTickets.length > 0) {
            const mine = filterTicketsAssignedToMe(originalTickets);
            setMyTickets(mine);
            const others = filterTicketsNotAssignedToMe(originalTickets);
            setTickets(others);
          }
        }
      } catch (_) {}
    }, 5000);
    return () => clearInterval(iv);
  }, [originalTickets, filterTicketsAssignedToMe, filterTicketsNotAssignedToMe]);

  // Build additional planned-only ticket entries (no Graph event) for today's view
  const plannedTicketEntries = useMemo(() => {
    try {
      const currentUserEmail = (typeof currentUser === 'string' ? currentUser : (currentUser?.userPrincipalName || currentUser?.mail || currentUser?.email || ''));

      const inToday = (startStr, endStr) => {
        if (!startStr || !endStr) return false;
        const start = new Date(startStr);
        const end = new Date(endStr);
        const now = new Date();
        const todayStart = new Date(now);
        todayStart.setHours(0, 0, 0, 0);
        const todayEnd = new Date(now);
        todayEnd.setHours(23, 59, 59, 999);
        return (start >= todayStart && start <= todayEnd) ||
               (end >= todayStart && end <= todayEnd) ||
               (start <= todayStart && end >= todayEnd);
      };

      const items = (originalTickets || [])
        .filter(t => {
          try {
            if (!t) return false;
            if (!t.isPlanned) return false;
            // Nur reine geplante Tickets ohne Graph-Verknüpfung
            if (t.calendarEventId) return false;
            if (!t.plannedStartDateTime || !t.plannedEndDateTime) return false;
            // Ausgeblendete/abgeschlossene Tickets nicht zeigen
            if (t.status === 'abgeschlossen') return false;
            // Nur für aktuellen Benutzer (wie CompactEventList-Filter)
            const assigned = (t.assignedTo || '').toLowerCase();
            const cur = (currentUserEmail || '').toLowerCase();
            if (!cur || !assigned || assigned !== cur) return false;
            // Nur heutige Fenster
            if (!inToday(t.plannedStartDateTime, t.plannedEndDateTime)) return false;
            return true;
          } catch (_) { return false; }
        })
        .map(t => {
          const startIso = new Date(t.plannedStartDateTime).toISOString();
          const endIso = new Date(t.plannedEndDateTime).toISOString();
          return {
            id: `planned:${t.id}`,
            subject: t.title || `Ticket ${t.ticketNumber}`,
            body: { content: `<div style="display:none;">[TICKET:${t.ticketNumber}]</div>` },
            start: { dateTime: startIso, timeZone: 'UTC' },
            end: { dateTime: endIso, timeZone: 'UTC' },
            // Meta für CompactEventList, um als verknüpft behandelt zu werden
            __ticket: t,
            __ticketInfo: {
              ticketId: t.id,
              ticketNumber: t.ticketNumber,
              status: t.status,
              assignedTo: t.assignedTo || null,
              calenderMarker: t.calenderMarker || null,
              projectId: t.projectId || null,
              subprojectId: t.subprojectId || null
            }
          };
        });
      return items;
    } catch (_) {
      return [];
    }
  }, [originalTickets, currentUser]);

  return {
    tickets, setTickets, myTickets, setMyTickets, setOriginalTickets,
    ticketSearchFilter, setTicketSearchFilter, myTicketSearchFilter, setMyTicketSearchFilter,
    optimisticHiddenIdsRef, filterTicketsAssignedToMe, filterTicketsNotAssignedToMe,
    manualRefreshTickets, ticketErrorCount, ticketsSuspended, ticketsStatus, plannedTicketEntries
  };
}
